import { FeatureFlags, IntegrationPartner, type IntegrationKey } from '@centric-os/types';
// Logos are imported directly, not via the @centric-os/app barrel, which pulls in the app router
import agilysys_logo from '@centric-os/app/src/assets/agilysys_logo.svg';
import amazon_logo from '@centric-os/app/src/assets/amazon_logo.svg';
import boost_logo from '@centric-os/app/src/assets/boost_logo.svg';
import centric_hub_logo from '@centric-os/app/src/assets/centric_hub_logo.svg';
import genius_logo from '@centric-os/app/src/assets/genius_logo.png';
import grubhub_logo from '@centric-os/app/src/assets/grubhub_logo.svg';
import illumia_logo from '@centric-os/app/src/assets/illumia_logo.svg';
import mashgin_logo from '@centric-os/app/src/assets/mashgin_logo.svg';
import ryst_logo from '@centric-os/app/src/assets/ryst_logo.svg';
import thrive_logo from '@centric-os/app/src/assets/thrive_logo.svg';
import time2eat_logo from '@centric-os/app/src/assets/time2eat_logo.svg';
import volante_logo from '@centric-os/app/src/assets/volante_logo.svg';
import { SitesRouteNames } from '../enums/router-names';
import { StationFormIDs } from '../enums/station-forms';

/**
 * Keys of `device_mapping` used by station integrations, as stored in Location.
 * TODO(CF-1807): use Location's type once its brand schema defines these keys.
 */
export type DeviceMappingKey = 'Agilysys' | 'Nextep' | 'Volante';

export interface IntegrationDefinition {
  /** Legacy partner identifier, being retired (CF-1805). */
  partner: IntegrationPartner;
  /** Display name wherever the integration is named in the UI. */
  displayName: string;
  /** Beta marker shown in the integration tile, page and "Add channel" dialog. */
  beta?: boolean;
  /** Uses its own custom integration tile that supports additional in-tile configurations. */
  customTile?: boolean;
  /**
   * Key of the integration's devices in `device_mapping`, for integrations that have devices.
   * Same value as `partner`, but kept separate because `partner` is being retired and these keys
   * are not: they're stored in Location.
   */
  deviceMappingKey?: DeviceMappingKey;
  logo: string;
  /** Config page route, when it isn't the generic integration page. */
  route?: SitesRouteNames;
  /** Hides the integration unless this flag is on. Edge can't check Harness flags, so CentricOS does. */
  featureFlag?: FeatureFlags;
  /** Form ID of the integration config page. Thrive and Boost use the app config page instead. */
  formId?: StationFormIDs;
}

/**
 * The one place station integrations are defined in CentricOS.
 * Keyed by the Edge SDK's integration keys, so a new Edge integration fails the build
 * until it has an entry here.
 *
 * Display order is the order of the entries below: there is no sort field. It sets the order
 * of the "Add channel" dialog, so place a new entry where it should appear, and move an entry
 * to reorder it. Station tags have their own order (see `integrationChecks` in use-station).
 */
export const INTEGRATION_DEFINITIONS: Record<IntegrationKey, IntegrationDefinition> = {
  thrive: {
    partner: IntegrationPartner.THRIVE,
    displayName: IntegrationPartner.THRIVE,
    logo: thrive_logo,
    route: SitesRouteNames.SITE_STATION_APP_CONFIG,
    customTile: true,
  },
  boost: {
    partner: IntegrationPartner.BOOST,
    displayName: IntegrationPartner.BOOST,
    logo: boost_logo,
    route: SitesRouteNames.SITE_STATION_APP_CONFIG,
    customTile: true,
  },
  nextep: {
    partner: IntegrationPartner.NEXTEP,
    displayName: 'Genius',
    logo: genius_logo,
    formId: StationFormIDs.STATION_INTEGRATION_NEXTEP,
    customTile: true,
    deviceMappingKey: 'Nextep',
  },
  volante: {
    partner: IntegrationPartner.VOLANTE,
    displayName: 'Volanté',
    logo: volante_logo,
    formId: StationFormIDs.STATION_INTEGRATION_VOLANTE,
    deviceMappingKey: 'Volante',
  },
  agilysys: {
    partner: IntegrationPartner.AGILYSYS,
    displayName: IntegrationPartner.AGILYSYS,
    logo: agilysys_logo,
    formId: StationFormIDs.STATION_INTEGRATION_AGILYSYS,
    deviceMappingKey: 'Agilysys',
  },
  agilysys_dod: {
    partner: IntegrationPartner.AGILYSYS_DOD,
    displayName: 'Agilysys DoD',
    logo: agilysys_logo,
    featureFlag: FeatureFlags.AGILYSYS_DOD_CENTRIC,
    formId: StationFormIDs.STATION_INTEGRATION_AGILYSYS_DOD,
  },
  mashgin: {
    partner: IntegrationPartner.MASHGIN,
    displayName: IntegrationPartner.MASHGIN,
    logo: mashgin_logo,
    formId: StationFormIDs.STATION_INTEGRATION_MASHGIN,
  },
  centric_hub: {
    partner: IntegrationPartner.CENTRIC_HUB,
    displayName: 'Centric Hub',
    beta: true,
    logo: centric_hub_logo,
    route: SitesRouteNames.SITE_STATION_CENTRIC_HUB,
    featureFlag: FeatureFlags.CENTRIC_HUB_VISIBLE,
    formId: StationFormIDs.STATION_INTEGRATION_CENTRIC_HUB,
  },
  grubhub: {
    partner: IntegrationPartner.GRUBHUB,
    displayName: 'Grubhub',
    logo: grubhub_logo,
    featureFlag: FeatureFlags.GRUB_HUB_CENTRIC_INTEGRATION,
    formId: StationFormIDs.STATION_INTEGRATION_GRUBHUB,
  },
  ryst: {
    partner: IntegrationPartner.RYST,
    displayName: 'Ryst',
    logo: ryst_logo,
    featureFlag: FeatureFlags.RYST_CENTRIC_INTEGRATION,
    formId: StationFormIDs.STATION_INTEGRATION_RYST,
  },
  illumia: {
    partner: IntegrationPartner.ILLUMIA,
    displayName: 'Illumia',
    logo: illumia_logo,
    formId: StationFormIDs.STATION_INTEGRATION_ILLUMIA,
  },
  amazon: {
    partner: IntegrationPartner.AMAZON,
    displayName: 'Amazon',
    logo: amazon_logo,
    featureFlag: FeatureFlags.AMAZON_INTEGRATION,
    formId: StationFormIDs.STATION_INTEGRATION_AMAZON,
  },
  time2eat: {
    partner: IntegrationPartner.TIME2EAT,
    displayName: 'Time2Eat',
    logo: time2eat_logo,
    featureFlag: FeatureFlags.TIME2EAT_INTEGRATION_UPDATE,
    formId: StationFormIDs.STATION_INTEGRATION_TIME2EAT,
  },
};

/** All station integration keys, in display order (Object.keys keeps the entries' order). */
export const INTEGRATION_KEYS = Object.keys(INTEGRATION_DEFINITIONS) as IntegrationKey[];
